// Entorno del navegador que usa `KypCapture`, inyectable para pruebas (Node sin DOM) y para
// integradores que necesiten adaptar algo (p. ej. su propio `fetch`).
import { deviceMotionSource, type MotionSource } from './ChallengeRunner.js';
import { createClock, sleep, type CaptureClock } from './clock.js';
import type { FetchLike } from './client.js';
import type { GuardDevice } from './CameraGuard.js';
import { VideoFrameSource, type FrameSource } from './FrameRing.js';
import { SessionKeys, type CaptureSigner } from './SessionKeys.js';
import { MediaRecorderChunkSource, type ChunkSource } from './VideoRecorder.js';

/** Lo mínimo de `navigator.mediaDevices`. */
export interface MediaDevicesLike {
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  enumerateDevices(): Promise<GuardDevice[]>;
}

/** Lo mínimo de `EventSource`. */
export interface EventSourceLike {
  addEventListener(type: string, listener: (ev: MessageEvent<string>) => void): void;
  close(): void;
  onerror: ((ev: Event) => void) | null;
}

export interface CaptureEnvironment {
  fetch: FetchLike;
  clock: CaptureClock;
  sleep: (ms: number) => Promise<void>;
  mediaDevices: MediaDevicesLike | null;
  createFrameSource(
    stream: MediaStream,
    video: HTMLVideoElement | null,
    clock: CaptureClock,
  ): FrameSource;
  createChunkSource(stream: MediaStream, clock: CaptureClock): ChunkSource;
  motion(): Promise<MotionSource | null>;
  barcode(video: HTMLVideoElement | null): (() => Promise<string | null>) | null;
  eventSource(url: string): EventSourceLike;
  generateKeys(): Promise<CaptureSigner>;
  /** SHA-256 del bundle cargado (`fetch(import.meta.url)`); `null` si no se puede. */
  bundleHash(): Promise<string | null>;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}

/** `<video>` silencioso que reproduce el stream cuando no hay interfaz con vista previa. */
function hiddenVideo(stream: MediaStream): HTMLVideoElement {
  const v = document.createElement('video');
  v.muted = true;
  v.playsInline = true;
  v.srcObject = stream;
  void v.play().catch(() => undefined);
  return v;
}

/** Entorno real del navegador. */
export function browserEnvironment(): CaptureEnvironment {
  const md = globalThis.navigator?.mediaDevices ?? null;
  return {
    fetch: (input, init) => globalThis.fetch(input, init),
    clock: createClock(),
    sleep,
    mediaDevices: md,
    createFrameSource: (stream, video, clock) =>
      new VideoFrameSource(video ?? hiddenVideo(stream), stream.getVideoTracks()[0]!, clock),
    createChunkSource: (stream, clock) => new MediaRecorderChunkSource(stream, clock),
    motion: deviceMotionSource,
    barcode: (video) => {
      const BD = (globalThis as { BarcodeDetector?: new () => BarcodeDetectorLike })
        .BarcodeDetector;
      if (!BD || !video) return null;
      let detector: BarcodeDetectorLike;
      try {
        detector = new BD();
      } catch {
        return null;
      }
      return async () => (await detector.detect(video))[0]?.rawValue ?? null;
    },
    eventSource: (url) => new EventSource(url) as unknown as EventSourceLike,
    generateKeys: () => SessionKeys.generate(),
    bundleHash: async () => {
      try {
        const res = await globalThis.fetch(import.meta.url);
        if (!res.ok) return null;
        const buf = new Uint8Array(await res.arrayBuffer());
        const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', buf));
        return [...digest].map((b) => b.toString(16).padStart(2, '0')).join('');
      } catch {
        return null;
      }
    },
  };
}

/** ¿`apiBase` apunta a localhost, `*.localhost`, 127.0.0.1 o una IP privada? (hooks de prueba). */
export function isLocalApiBase(apiBase: string): boolean {
  let host: string;
  try {
    host = new URL(apiBase).hostname.toLowerCase();
  } catch {
    return false;
  }
  host = host.replace(/^\[|\]$/g, '');
  if (host === 'localhost' || host.endsWith('.localhost') || host === '::1') return true;
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (m) {
    const [a, b] = [Number(m[1]), Number(m[2])];
    return a === 127 || a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
  }
  return /^f[cd][0-9a-f]{2}:/.test(host);
}
